// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pick the model a coding agent runs on, and its reasoning level and service tier, from
// what the machine reports it can run (konvo-agents 0.9.0+; tiers and labels 0.13.0+). The model
// menu has a rail of starred models and providers, search, logos, New badges and Legacy models.
// "Project default" leaves a new thread to the project's setting in the agent tool; an open thread
// shows the model it runs on until another is picked. In New agent chat and a thread's composer it
// is one pill chip ("Opus 5.5 | High") opening one menu: the model list with Reasoning and Fast mode
// under it. The Assistant's agent review card has labelled fields with a model menu and a
// Reasoning / Service tier menu.

import {ProviderLogo} from '@app/features/konvo_agents/components/AgentBrand';
import {CONNECTION_DETAILS, connectionLabel} from '@app/features/konvo_agents/components/AgentConnectionCopy';
import {openAgentConnections} from '@app/features/konvo_agents/components/AgentConnections';
import styles from '@app/features/konvo_agents/components/AgentModelPicker.module.css';
import {type AgentAccount, machineAccounts} from '@app/features/konvo_agents/state/AgentAccounts';
import {machineConnections, visibleModels} from '@app/features/konvo_agents/state/AgentConnections';
import AgentModelFavourites, {modelKey} from '@app/features/konvo_agents/state/AgentModelFavourites';
import Agents from '@app/features/konvo_agents/state/Agents';
import {defaultModelOf, modelFor, savedModelOf} from '@app/features/konvo_agents/state/AgentsModel';
import {revealInList} from '@app/features/konvo_agents/utils/AgentListScroll';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import type {
	AgentConnection,
	AgentMachineView,
	AgentModel,
	AgentModelChoice,
	AgentModelOption,
} from '@konvo/schema/src/domains/konvo/Agents';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	IconBolt,
	IconCheck,
	IconChevronDown,
	IconChevronLeft,
	IconChevronRight,
	IconFolder,
	IconSearch,
	IconStar,
	IconStarFilled,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';

const EFFORTS: Record<string, ReturnType<typeof msg>> = {
	minimal: msg({message: 'Minimal', comment: 'Agent model picker: reasoning level.'}),
	low: msg({message: 'Low', comment: 'Agent model picker: reasoning level.'}),
	medium: msg({message: 'Medium', comment: 'Agent model picker: reasoning level.'}),
	high: msg({message: 'High', comment: 'Agent model picker: reasoning level.'}),
	xhigh: msg({message: 'Extra high', comment: 'Agent model picker: reasoning level.'}),
	max: msg({message: 'Max', comment: 'Agent model picker: reasoning level.'}),
	ultra: msg({message: 'Ultra', comment: 'Agent model picker: reasoning level.'}),
};
const TIERS: Record<string, ReturnType<typeof msg>> = {
	default: msg({message: 'Standard', comment: 'Agent model picker: the normal service tier of a model.'}),
	priority: msg({
		message: 'Fast',
		comment: 'Agent model picker: the faster service tier of a model, which uses more of the plan.',
	}),
};
const DEFAULT_MODEL = msg({
	message: 'Project default ({model})',
	comment: 'Agent model picker: run on the model the project uses; {model} names it, e.g. "Claude Opus 5.5".',
});
const DEFAULT_MODEL_UNKNOWN = msg({
	message: 'Project default',
	comment: 'Agent model picker: run on the model the project uses, when Konvo does not know which one it is.',
});
const LEGACY_COUNT = msg({
	message: '{count, plural, one {# model} other {# models}}',
	comment: 'Agent model picker: how many older models sit under "Legacy models".',
});

/** "Extra high" for xhigh; a level Konvo has no word for shows as the agent tool names it. */
export function effortLabel(i18n: I18n, effort: string, options?: ReadonlyArray<AgentModelOption>): string {
	const known = EFFORTS[effort];
	if (known) return i18n._(known);
	const named = options?.find((option) => option.id === effort)?.label;
	return named || effort.charAt(0).toUpperCase() + effort.slice(1);
}

/** "Fast" for priority; a tier Konvo has no word for shows as the agent tool names it. */
export function serviceTierLabel(i18n: I18n, tier: string, options?: ReadonlyArray<AgentModelOption>): string {
	const known = TIERS[tier];
	if (known) return i18n._(known);
	return options?.find((option) => option.id === tier)?.label || tier;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** The pick carried over to another model: its level and tier when that model has them too. */
function switchModel(next: AgentModel, from: AgentModelChoice | null): AgentModelChoice {
	const effort = from?.effort && next.efforts?.includes(from.effort) ? from.effort : undefined;
	const serviceTier =
		from?.serviceTier && next.serviceTiers?.some((tier) => tier.id === from.serviceTier) ? from.serviceTier : undefined;
	return {
		provider: next.provider,
		model: next.model,
		...(effort ? {effort} : {}),
		...(serviceTier ? {serviceTier} : {}),
	};
}

type Tab = {kind: 'favourites'} | {kind: 'provider'; provider: string; name: string};
type Row = {kind: 'default'} | {kind: 'model'; model: AgentModel} | {kind: 'legacy'; count: number};

const ModelMenu = observer(
	({
		models,
		shown,
		home,
		defaultName,
		allowDefault,
		phone,
		onPick,
		onClose,
		dataFlx,
		accounts,
		usedUp,
		connections,
		onDiagnostics,
		disabledProviders,
	}: {
		models: Array<AgentModel>;
		connections: Array<AgentConnection>;
		disabledProviders: Set<string>;
		onDiagnostics?: () => void;
		/** The machine's logins, by provider instance, when it has two of an agent. */
		accounts: Map<string, AgentAccount>;
		/** Logins whose plan is used up: a red dot on their tab. */
		usedUp: Set<string>;
		/** The model picked (or the thread's own); null when "Project default" is. */
		shown: AgentModel | null;
		/** The model that runs, whose provider is used when no remembered tab is available. */
		home: AgentModel | null;
		defaultName: string | null;
		/** A new thread can leave it to the project; an open thread always runs on a model. */
		allowDefault: boolean;
		phone: boolean;
		onPick: (model: AgentModel | null) => void;
		onClose: () => void;
		dataFlx?: string;
	}) => {
		const {i18n, t} = useLingui();
		const [query, setQuery] = useState('');
		const providers = useMemo(() => {
			const seen = new Map<string, string>();
			for (const model of models) if (!seen.has(model.provider)) seen.set(model.provider, model.providerName);
			for (const connection of connections)
				if (!seen.has(connection.provider)) seen.set(connection.provider, connection.providerName);
			return [...seen].map(([provider, name]) => ({kind: 'provider' as const, provider, name}));
		}, [models, connections]);
		const favourites = models.filter((model) => AgentModelFavourites.has(model));
		const tabs: Array<Tab> = [...(favourites.length ? [{kind: 'favourites' as const}] : []), ...providers];
		const remembered = AgentModelFavourites.lastTab;
		// A remembered provider may be unavailable on this machine, or the last favourite unstarred.
		const tab: Tab = tabs.find(
			(entry) =>
				entry.kind === remembered?.kind &&
				(entry.kind === 'favourites' || (remembered.kind === 'provider' && entry.provider === remembered.provider)),
		) ??
			providers.find((entry) => entry.provider === home?.provider) ??
			tabs[0] ?? {kind: 'favourites'};
		const [legacyOpen, setLegacyOpen] = useState(false);
		const [active, setActive] = useState(0);
		const list = useRef<HTMLDivElement | null>(null);
		const search = useRef<HTMLInputElement | null>(null);

		const needle = query.trim().toLocaleLowerCase();
		const rows: Array<Row> = (() => {
			if (needle)
				return models
					.filter((model) =>
						[model.name, model.model, model.providerName, ...(model.aliases ?? [])].some((name) =>
							name.toLocaleLowerCase().includes(needle),
						),
					)
					.map((model) => ({kind: 'model', model}));
			if (tab.kind === 'favourites')
				return [
					...(allowDefault ? [{kind: 'default' as const}] : []),
					...favourites.map((model) => ({kind: 'model' as const, model})),
				];
			const own = models.filter((model) => model.provider === tab.provider);
			const legacy = own.filter((model) => model.legacy);
			if (legacyOpen) return legacy.map((model) => ({kind: 'model', model}));
			return [
				...(allowDefault ? [{kind: 'default' as const}] : []),
				...own.filter((model) => !model.legacy).map((model) => ({kind: 'model' as const, model})),
				...(legacy.length ? [{kind: 'legacy' as const, count: legacy.length}] : []),
			];
		})();
		const at = Math.min(active, Math.max(rows.length - 1, 0));
		// ⌘1-9 pick the first models listed, as in T3 Code.
		const numbered = rows
			.filter(
				(row): row is Extract<Row, {kind: 'model'}> =>
					row.kind === 'model' && !disabledProviders.has(row.model.provider),
			)
			.slice(0, 9);

		useEffect(() => {
			revealInList(list.current, list.current?.querySelector<HTMLElement>(`[data-row="${at}"]`));
		}, [at]);
		useEffect(() => {
			// The phone's keyboard would cover the list; on desktop the menu opens to type.
			if (!phone) search.current?.focus();
		}, [phone]);

		const choose = (row: Row | undefined) => {
			if (!row) return;
			if (row.kind === 'model' && disabledProviders.has(row.model.provider)) return;
			if (row.kind === 'legacy') {
				setLegacyOpen(true);
				setActive(0);
				return;
			}
			onPick(row.kind === 'model' ? row.model : null);
			onClose();
		};
		const onKeyDown = (event: React.KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				if (legacyOpen) setLegacyOpen(false);
				else onClose();
				return;
			}
			if ((event.metaKey || event.ctrlKey) && /^[1-9]$/.test(event.key)) {
				const row = numbered[Number(event.key) - 1];
				if (row) {
					event.preventDefault();
					choose(row);
				}
				return;
			}
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				if (!rows.length) return;
				setActive((at + (event.key === 'ArrowDown' ? 1 : rows.length - 1)) % rows.length);
				return;
			}
			if (event.key === 'Enter' && event.target === search.current) {
				event.preventDefault();
				choose(rows[at]);
			}
		};

		const tabLabel = (entry: Tab) => (entry.kind === 'favourites' ? t`Starred` : entry.name);
		const sameTab = (a: Tab, b: Tab) =>
			a.kind === b.kind && (a.kind === 'favourites' || (b.kind === 'provider' && a.provider === b.provider));
		return (
			// biome-ignore lint/a11y/noStaticElementInteractions: keys from the search and the rows move through the list.
			<div
				className={clsx(styles.menu, styles.modelMenu, phone && styles.menuPhone)}
				onKeyDown={onKeyDown}
				data-flx={dataFlx ? `${dataFlx}.model-menu` : undefined}
			>
				{tabs.length > 1 && !needle && (
					<div className={styles.rail} role="tablist" aria-label={t`Providers`}>
						{tabs.map((entry, index) => (
							<button
								key={entry.kind === 'favourites' ? 'favourites' : entry.provider}
								type="button"
								role="tab"
								className={clsx(styles.railTab, sameTab(entry, tab) && styles.railTabOn)}
								aria-selected={sameTab(entry, tab)}
								aria-label={tabLabel(entry)}
								title={tabLabel(entry)}
								onClick={() => {
									AgentModelFavourites.setLastTab(entry);
									setLegacyOpen(false);
									setActive(0);
								}}
								data-flx={dataFlx ? `${dataFlx}.model-menu.tab` : undefined}
							>
								{entry.kind === 'favourites' ? (
									<IconStarFilled size={19} className={styles.railStar} />
								) : (
									<ProviderLogo
										provider={entry.provider}
										size={21}
										account={accounts.get(entry.provider)}
										out={usedUp.has(entry.provider)}
									/>
								)}
								{index === 0 && favourites.length > 0 && <span className={styles.railRule} aria-hidden />}
							</button>
						))}
					</div>
				)}
				<div className={styles.pane}>
					<label className={styles.search}>
						<IconSearch size={16} stroke={1.8} aria-hidden />
						<input
							ref={search}
							value={query}
							onChange={(event) => {
								setQuery(event.target.value);
								setActive(0);
							}}
							placeholder={t`Search models…`}
							aria-label={t`Search models`}
							data-flx={dataFlx ? `${dataFlx}.model-menu.search` : undefined}
						/>
					</label>
					{tab.kind === 'provider' && disabledProviders.has(tab.provider) && (
						<p className={styles.connectionStatus} role="status">
							{connectionLabel(i18n, connections.find((c) => c.provider === tab.provider)?.status ?? 'unavailable')}
						</p>
					)}
					{legacyOpen && !needle && (
						<button
							type="button"
							className={styles.back}
							onClick={() => {
								setLegacyOpen(false);
								setActive(0);
							}}
							data-flx={dataFlx ? `${dataFlx}.model-menu.back` : undefined}
						>
							<IconChevronLeft size={16} stroke={2} />
							{t`Legacy models`}
						</button>
					)}
					<div ref={list} className={styles.list} role="listbox" aria-label={t`Models`}>
						{rows.length === 0 && <p className={styles.empty}>{t`No models match.`}</p>}
						{rows.map((row, index) => {
							const common = {
								'data-row': index,
								className: clsx(styles.row, index === at && styles.rowActive),
								onMouseMove: () => index !== at && setActive(index),
							};
							if (row.kind === 'legacy')
								return (
									<button
										key="legacy"
										type="button"
										{...common}
										onClick={() => choose(row)}
										data-flx={dataFlx ? `${dataFlx}.model-menu.legacy` : undefined}
									>
										<span className={styles.rowText}>
											<span className={styles.rowName}>{t`Legacy models`}</span>
											<span className={styles.rowSub}>{i18n._(LEGACY_COUNT, {count: row.count})}</span>
										</span>
										<IconChevronRight size={18} stroke={1.8} className={styles.rowEnd} />
									</button>
								);
							if (row.kind === 'default') {
								const isPicked = shown === null;
								return (
									<button
										key="default"
										type="button"
										role="option"
										aria-selected={isPicked}
										{...common}
										onClick={() => choose(row)}
										data-flx={dataFlx ? `${dataFlx}.model-menu.default` : undefined}
									>
										<span className={styles.rowText}>
											<span className={styles.rowName}>
												{t`Project default`}
												{isPicked && <IconCheck size={15} stroke={2.2} className={styles.check} />}
											</span>
											<span className={styles.rowSub}>
												<IconFolder size={13} stroke={1.8} />
												{defaultName ?? t`The project's own setting`}
											</span>
										</span>
									</button>
								);
							}
							const {model} = row;
							const disabled = disabledProviders.has(model.provider);
							const {name} = model;
							const isPicked = shown !== null && modelKey(shown) === modelKey(model);
							const starred = AgentModelFavourites.has(model);
							const number = numbered.indexOf(row);
							return (
								<div
									key={modelKey(model)}
									role="option"
									aria-disabled={disabled}
									aria-selected={isPicked}
									tabIndex={-1}
									{...common}
									onClick={() => choose(row)}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											choose(row);
										}
									}}
									data-flx={dataFlx ? `${dataFlx}.model-menu.model` : undefined}
								>
									<span className={styles.rowText}>
										<span className={styles.rowName}>
											<span className={styles.rowNameText}>{model.name}</span>
											{model.badge?.toLowerCase() === 'new' && <span className={styles.badgeNew}>{t`New`}</span>}
											{isPicked && <IconCheck size={15} stroke={2.2} className={styles.check} />}
										</span>
										<span className={styles.rowSub}>
											<ProviderLogo provider={model.provider} size={13} account={accounts.get(model.provider)} />
											{model.providerName}
											{disabled &&
												` · ${connectionLabel(i18n, connections.find((c) => c.provider === model.provider)?.status ?? 'unavailable')}`}
										</span>
									</span>
									{!phone && number >= 0 && (
										<kbd className={styles.shortcut}>
											{isMac ? '⌘' : 'Ctrl+'}
											{number + 1}
										</kbd>
									)}
									<button
										type="button"
										className={clsx(styles.star, starred && styles.starOn)}
										aria-pressed={starred}
										aria-label={starred ? t`Unstar ${name}` : t`Star ${name}`}
										title={starred ? t`Unstar` : t`Star`}
										onClick={(event) => {
											event.stopPropagation();
											AgentModelFavourites.toggle(model);
										}}
										data-flx={dataFlx ? `${dataFlx}.model-menu.star` : undefined}
									>
										{starred ? <IconStarFilled size={16} /> : <IconStar size={16} stroke={1.7} />}
									</button>
								</div>
							);
						})}
					</div>
					{onDiagnostics && (
						<button
							type="button"
							className={styles.back}
							onClick={() => {
								onClose();
								onDiagnostics();
							}}
							data-flx="konvo-agents.model.connection-details"
						>
							{i18n._(CONNECTION_DETAILS)}
						</button>
					)}
				</div>
			</div>
		);
	},
);

function OptionRow({
	label,
	description,
	picked,
	isDefault,
	onPick,
	dataFlx,
}: {
	label: string;
	description?: string;
	picked: boolean;
	isDefault: boolean;
	onPick: () => void;
	dataFlx?: string;
}) {
	const {t} = useLingui();
	return (
		<button
			type="button"
			role="menuitemradio"
			aria-checked={picked}
			className={clsx(styles.option, picked && styles.optionOn)}
			onClick={onPick}
			data-flx={dataFlx}
		>
			<span className={styles.rowText}>
				<span className={styles.rowName}>
					{label}
					{isDefault && <span className={styles.badgeDefault}>{t`Default`}</span>}
				</span>
				{description && <span className={styles.rowSub}>{description}</span>}
			</span>
			{picked && <IconCheck size={16} stroke={2.2} className={styles.check} />}
		</button>
	);
}

/** Reasoning and Service tier for the model, as T3 Code's composer menu shows them. */
function TraitsMenu({
	model,
	effort,
	serviceTier,
	phone,
	onPick,
	onClose,
	dataFlx,
}: {
	model: AgentModel;
	effort: string | undefined;
	serviceTier: string | undefined;
	phone: boolean;
	onPick: (change: {effort?: string; serviceTier?: string}) => void;
	onClose: () => void;
	dataFlx?: string;
}) {
	const {i18n, t} = useLingui();
	const box = useRef<HTMLDivElement | null>(null);
	const efforts = model.efforts ?? [];
	const tiers = model.serviceTiers ?? [];
	useEffect(() => {
		box.current?.querySelector<HTMLElement>('[aria-checked="true"], button')?.focus();
	}, []);
	const onKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			onClose();
			return;
		}
		if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
		event.preventDefault();
		const items = [...(box.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitemradio"]') ?? [])];
		const index = items.indexOf(document.activeElement as HTMLButtonElement);
		items[(index + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
	};
	const description = (options: ReadonlyArray<AgentModelOption> | undefined, id: string) =>
		options?.find((option) => option.id === id)?.description;
	return (
		<div
			ref={box}
			role="menu"
			className={clsx(styles.menu, styles.traitsMenu, phone && styles.menuPhone)}
			onKeyDown={onKeyDown}
			data-flx={dataFlx ? `${dataFlx}.traits-menu` : undefined}
		>
			{efforts.length > 0 && (
				<div role="group" aria-label={t`Reasoning`}>
					<div className={styles.heading}>{t`Reasoning`}</div>
					{efforts.map((id) => (
						<OptionRow
							key={id}
							label={effortLabel(i18n, id, model.effortOptions)}
							description={description(model.effortOptions, id)}
							picked={id === effort}
							isDefault={id === model.defaultEffort}
							onPick={() => {
								onPick({effort: id});
								onClose();
							}}
							dataFlx={dataFlx ? `${dataFlx}.traits-menu.effort` : undefined}
						/>
					))}
				</div>
			)}
			{efforts.length > 0 && tiers.length > 0 && <div className={styles.separator} aria-hidden />}
			{tiers.length > 0 && (
				<div role="group" aria-label={t`Service tier`}>
					<div className={styles.heading}>{t`Service tier`}</div>
					{tiers.map((tier) => (
						<OptionRow
							key={tier.id}
							label={serviceTierLabel(i18n, tier.id, tiers)}
							description={tier.description}
							picked={tier.id === serviceTier}
							isDefault={tier.id === model.defaultServiceTier}
							onPick={() => {
								onPick({serviceTier: tier.id});
								onClose();
							}}
							dataFlx={dataFlx ? `${dataFlx}.traits-menu.tier` : undefined}
						/>
					))}
				</div>
			)}
		</div>
	);
}

/** Levels the phone's one menu leaves out (JP 2026-10-29): picked on a computer, they still show by name. */
const PHONE_HIDDEN_EFFORTS = new Set(['ultracode', 'ultrathink']);
const FAST_MODE = msg({
	message: 'Fast mode',
	comment: 'Agent model menu: switch for the faster service tier.',
});
const COMBO_LABEL = msg({
	message: 'Model: {modelName}, reasoning: {traitsLabel}',
	comment: 'Agent composer: the one chip for model and reasoning, e.g. "Model: Claude Opus 5.5, reasoning: High".',
});
const COMBO_FAST = msg({
	message: '{traitsLabel}, Fast',
	comment: 'Agent composer: the reasoning part of the chip\'s label when the fast tier is on, e.g. "High, Fast".',
});
const FAST_HINT = msg({
	message: 'Quicker, uses more of your plan',
	comment: 'Agent model menu: under the Fast mode switch, when the agent tool gives no description.',
});

/** "Opus 5.5" for "Claude Opus 5.5": the logo beside it already says Claude. */
function shortModelName(model: AgentModel): string {
	const brand = model.providerName.split(' · ')[0]?.trim();
	return brand && model.name.startsWith(`${brand} `) ? model.name.slice(brand.length + 1) : model.name;
}

/**
 * Under the model list in the one menu (design/agents-phone-compact): Reasoning as tabs and the fast
 * tier as a switch. It keeps its own pick, since the menu shows what it had when it opened, and hands
 * the whole choice on with each change. The menu stays open.
 */
function ComboTraits({
	model,
	effort: startEffort,
	serviceTier: startTier,
	keepTier,
	phone,
	onChange,
	dataFlx,
}: {
	model: AgentModel;
	effort: string | undefined;
	serviceTier: string | undefined;
	/** The choice names a tier already: keep naming it. */
	keepTier: boolean;
	phone: boolean;
	onChange: (value: AgentModelChoice) => void;
	dataFlx?: string;
}) {
	const {i18n, t} = useLingui();
	const [effort, setEffort] = useState(startEffort);
	const [tier, setTier] = useState(startTier);
	const [tierPicked, setTierPicked] = useState(keepTier);
	const efforts = (model.efforts ?? []).filter((id) => !phone || !PHONE_HIDDEN_EFFORTS.has(id) || id === effort);
	const tiers = model.serviceTiers ?? [];
	// Standard and Fast: a switch. Other tiers: tabs, as the levels.
	const fastSwitch = tiers.length === 2 && tiers.some((x) => x.id === 'priority');
	const push = (next: {effort?: string; tier?: string; tierPicked?: boolean}) => {
		const nextEffort = next.effort ?? effort;
		const nextTier = next.tier ?? tier;
		onChange({
			provider: model.provider,
			model: model.model,
			...(nextEffort && (model.efforts ?? []).includes(nextEffort) ? {effort: nextEffort} : {}),
			...((next.tierPicked ?? tierPicked) && nextTier ? {serviceTier: nextTier} : {}),
		});
	};
	const pickEffort = (id: string) => {
		setEffort(id);
		push({effort: id});
	};
	const pickTier = (id: string) => {
		setTier(id);
		setTierPicked(true);
		push({tier: id, tierPicked: true});
	};
	const tabs = (
		ids: ReadonlyArray<string>,
		on: string | undefined,
		label: (id: string) => string,
		pick: (id: string) => void,
		flx: string,
	) => (
		<div className={styles.tabs} role="radiogroup">
			{ids.map((id) => (
				<button
					key={id}
					type="button"
					role="radio"
					aria-checked={id === on}
					className={clsx(styles.tab, id === on && styles.tabOn)}
					onClick={() => pick(id)}
					data-flx={dataFlx ? `${dataFlx}.traits.${flx}` : undefined}
				>
					{label(id)}
				</button>
			))}
		</div>
	);
	const priority = tiers.find((x) => x.id === 'priority');
	const fastOn = tier === 'priority';
	return (
		<div className={styles.traits} data-flx={dataFlx ? `${dataFlx}.traits` : undefined}>
			{efforts.length > 0 && (
				<div className={styles.traitsSection} role="group" aria-label={t`Reasoning`}>
					<div className={styles.traitsHead}>
						{t`Reasoning`}
						<span>{effort ? effortLabel(i18n, effort, model.effortOptions) : ''}</span>
					</div>
					{tabs(efforts, effort, (id) => effortLabel(i18n, id, model.effortOptions), pickEffort, 'effort')}
				</div>
			)}
			{tiers.length > 0 && fastSwitch && priority && (
				<button
					type="button"
					role="switch"
					aria-checked={fastOn}
					className={styles.switchRow}
					onClick={() => pickTier(fastOn ? (tiers.find((x) => x.id !== 'priority')?.id ?? 'default') : 'priority')}
					data-flx={dataFlx ? `${dataFlx}.traits.fast` : undefined}
				>
					<IconBolt size={19} stroke={1.7} className={styles.switchIcon} aria-hidden />
					<span className={styles.switchText}>
						{i18n._(FAST_MODE)}
						<span>{priority.description || i18n._(FAST_HINT)}</span>
					</span>
					<span className={clsx(styles.switch, fastOn && styles.switchOn)} aria-hidden />
				</button>
			)}
			{tiers.length > 0 && !fastSwitch && (
				<div className={styles.traitsSection} role="group" aria-label={t`Service tier`}>
					<div className={styles.traitsHead}>{t`Service tier`}</div>
					{tabs(
						tiers.map((x) => x.id),
						tier,
						(id) => serviceTierLabel(i18n, id, tiers),
						pickTier,
						'tier',
					)}
				</div>
			)}
		</div>
	);
}

export const AgentModelPicker = observer(
	({
		machine,
		projectId,
		value,
		onChange,
		current = null,
		variant = 'chip',
		chipClassName,
		phone = false,
		compact = false,
		dataFlx,
		defaultName,
	}: {
		machine: AgentMachineView | null;
		/** The project a new thread starts in, for the default; null for an existing thread. */
		projectId?: string | null;
		value: AgentModelChoice | null;
		onChange: (value: AgentModelChoice | null) => void;
		/** An open thread: the model it runs on now. It keeps its provider, and there is no "Project default". */
		current?: AgentModelChoice | null;
		/** Chips in a composer, or labelled fields in a form. */
		variant?: 'chip' | 'field';
		/** The composer's own chip style. */
		chipClassName?: string;
		phone?: boolean;
		/** The model as its provider's logo only, named in its tooltip (a composer at rest on a phone). */
		compact?: boolean;
		dataFlx?: string;
		/** What the default runs on when the machine list cannot say (the Assistant's card names it). */
		defaultName?: string | null;
	}) => {
		const {i18n, t} = useLingui();
		const all = visibleModels(machine);
		const connections = machineConnections(machine);
		const locked = current?.provider;
		const models = locked ? all.filter((model) => model.provider === locked) : all;
		const shownConnections = locked ? connections.filter((c) => c.provider === locked) : connections;
		if (models.length === 0 && shownConnections.length === 0) return null;
		const disabledProviders = new Set(
			connections.filter((c) => !machine?.online || c.status !== 'ready').map((c) => c.provider),
		);
		const defaultChoice = current ? null : defaultModelOf(machine, projectId);
		const fallback = modelFor(machine, defaultChoice);
		// With no default saved, the first model is already selected: there is no default to go back to.
		const allowDefault = !current && (!value || Boolean(savedModelOf(machine, projectId)));
		// What runs: the pick, else the thread's own model, else the project's default.
		const base = value ?? current ?? defaultChoice;
		const choice = value ?? current;
		const chosen = choice
			? (models.find((m) => m.provider === choice.provider && m.model === choice.model) ?? null)
			: null;
		const running = modelFor(machine, base);
		const fallbackName = fallback?.name ?? defaultName ?? null;
		const efforts = running?.efforts ?? [];
		const tiers = running?.serviceTiers ?? [];
		const effort = base?.effort ?? running?.defaultEffort;
		const serviceTier = base?.serviceTier ?? running?.defaultServiceTier;
		const fast = serviceTier === 'priority';
		const traitsLabel = efforts.length
			? effort
				? effortLabel(i18n, effort, running?.effortOptions)
				: t`Reasoning`
			: serviceTierLabel(i18n, serviceTier ?? '', tiers) || t`Service tier`;
		const field = variant === 'field';
		// Two logins of one agent on the machine: which one each model runs on.
		const accounts = machineAccounts(machine);
		const usedUp = machine ? Agents.usedUpLogins(machine.id) : new Set<string>();
		const trigger = clsx(
			field ? styles.fieldButton : (chipClassName ?? styles.chip),
			phone && !field && styles.chipPhone,
		);
		const modelName =
			chosen?.name ?? (fallbackName ? i18n._(DEFAULT_MODEL, {model: fallbackName}) : i18n._(DEFAULT_MODEL_UNKNOWN));
		// Without a logo to show, the name stays.
		const logoOnly = compact && !field && Boolean(chosen ?? fallback);
		const modelPicker = (
			<Popout
				position={field ? 'bottom-start' : 'top-start'}
				offsetMainAxis={6}
				render={({onClose}) => (
					<ModelMenu
						models={models}
						connections={shownConnections}
						disabledProviders={disabledProviders}
						onDiagnostics={machine ? () => openAgentConnections(machine.id) : undefined}
						accounts={accounts}
						usedUp={usedUp}
						shown={chosen}
						home={running}
						defaultName={fallbackName}
						allowDefault={allowDefault}
						phone={phone}
						onPick={(model) => onChange(model ? switchModel(model, base) : null)}
						onClose={onClose}
						dataFlx={dataFlx}
					/>
				)}
			>
				<button
					type="button"
					className={trigger}
					aria-haspopup="listbox"
					aria-label={t`Model: ${modelName}`}
					title={logoOnly ? modelName : t`Model`}
					data-flx={dataFlx ? `${dataFlx}.model` : undefined}
				>
					{(chosen ?? fallback) && (
						<ProviderLogo
							provider={(chosen ?? fallback)!.provider}
							size={15}
							account={accounts.get((chosen ?? fallback)!.provider)}
						/>
					)}
					{!logoOnly && (
						<span className={styles.chipText}>{chosen?.name ?? fallbackName ?? i18n._(DEFAULT_MODEL_UNKNOWN)}</span>
					)}
					<IconChevronDown size={14} stroke={2} className={styles.caret} aria-hidden />
				</button>
			</Popout>
		);
		const traits = running && (efforts.length > 0 || tiers.length > 0) && (
			<Popout
				position={field ? 'bottom-start' : 'top-start'}
				offsetMainAxis={6}
				render={({onClose}) => (
					<TraitsMenu
						model={running}
						effort={effort}
						serviceTier={serviceTier}
						phone={phone}
						onPick={(change) => {
							const next: AgentModelChoice = {
								provider: running.provider,
								model: running.model,
								...(effort && efforts.includes(effort) ? {effort} : {}),
								...(base?.serviceTier ? {serviceTier: base.serviceTier} : {}),
								...change,
							};
							onChange(next);
						}}
						onClose={onClose}
						dataFlx={dataFlx}
					/>
				)}
			>
				<button
					type="button"
					className={trigger}
					aria-haspopup="menu"
					aria-label={fast ? t`Reasoning: ${traitsLabel}, Fast` : t`Reasoning: ${traitsLabel}`}
					title={efforts.length ? t`Reasoning and speed` : t`Service tier`}
					data-flx={dataFlx ? `${dataFlx}.effort` : undefined}
				>
					{fast && <IconBolt size={15} stroke={1.9} className={styles.bolt} aria-hidden />}
					<span className={styles.chipText}>{traitsLabel}</span>
					<IconChevronDown size={14} stroke={2} className={styles.caret} aria-hidden />
				</button>
			</Popout>
		);
		// One chip ("Opus 5.5 | High", the logo alone at rest on a phone) and one menu, the model list
		// with Reasoning and Fast mode under it (JP 2026-10-29 on phones, 2026-10-30 on computers).
		if (!field) {
			const shownModel = chosen ?? fallback;
			const shortName = shownModel ? shortModelName(shownModel) : i18n._(DEFAULT_MODEL_UNKNOWN);
			const hasTraits = !!running && (efforts.length > 0 || tiers.length > 0);
			return (
				<Popout
					position="top-start"
					offsetMainAxis={6}
					render={({onClose}) => (
						<div
							className={clsx(styles.menu, styles.combo, !phone && styles.comboWide)}
							data-flx={dataFlx ? `${dataFlx}.combo-menu` : undefined}
						>
							<ModelMenu
								models={models}
								connections={shownConnections}
								disabledProviders={disabledProviders}
								onDiagnostics={machine ? () => openAgentConnections(machine.id) : undefined}
								accounts={accounts}
								usedUp={usedUp}
								shown={chosen}
								home={running}
								defaultName={fallbackName}
								allowDefault={allowDefault}
								phone={phone}
								onPick={(model) => onChange(model ? switchModel(model, base) : null)}
								onClose={onClose}
								dataFlx={dataFlx}
							/>
							{hasTraits && running && (
								<ComboTraits
									model={running}
									effort={effort}
									serviceTier={serviceTier}
									keepTier={!!base?.serviceTier}
									phone={phone}
									onChange={onChange}
									dataFlx={dataFlx}
								/>
							)}
						</div>
					)}
				>
					<button
						type="button"
						className={clsx(trigger, styles.comboChip, logoOnly && styles.comboChipLogo)}
						aria-haspopup="dialog"
						aria-label={
							hasTraits
								? i18n._(COMBO_LABEL, {
										modelName,
										traitsLabel: fast ? i18n._(COMBO_FAST, {traitsLabel}) : traitsLabel,
									})
								: t`Model: ${modelName}`
						}
						title={modelName}
						data-flx={dataFlx ? `${dataFlx}.model` : undefined}
					>
						{shownModel && (
							<ProviderLogo provider={shownModel.provider} size={15} account={accounts.get(shownModel.provider)} />
						)}
						{!logoOnly && (
							<>
								<span className={styles.chipText}>{shortName}</span>
								{hasTraits && (
									<>
										<span className={styles.comboSep} aria-hidden />
										{fast && <IconBolt size={14} stroke={1.9} className={styles.bolt} aria-hidden />}
										<span className={clsx(styles.chipText, styles.comboTraits)}>{traitsLabel}</span>
									</>
								)}
								<IconChevronDown size={14} stroke={2} className={styles.caret} aria-hidden />
							</>
						)}
					</button>
				</Popout>
			);
		}
		return (
			<div className={styles.field}>
				<span className={styles.fieldLabel}>{t`Model`}</span>
				<div className={styles.fieldRow}>
					{modelPicker}
					{traits}
				</div>
			</div>
		);
	},
);
